// ─────────────────────────────────────────────────────────────────────────────
// CRM 2.0 — BulkResultDialog
//
// Renders the per-record outcome of a bulk operation: how many records were
// updated, and which were skipped with their reasons (Req 8.4). Skipped records
// are grouped by reason so a large selection stays readable. Purely presentational.
// ─────────────────────────────────────────────────────────────────────────────

import { useMemo } from 'react';
import { CheckCircle2, SkipForward } from 'lucide-react';
import { Modal } from '@/shared/components/ui/Modal';
import { Button } from '@/shared/components/ui/Button';
import type { BulkResponse } from '../types';

export interface BulkResultDialogProps {
  open: boolean;
  onClose: () => void;
  /** The response from POST /api/v2/bulk/:entity, or null before any submission. */
  result: BulkResponse | null;
  /** Display noun for the entity (e.g. "lead"). */
  entityNoun: string;
}

export function BulkResultDialog({ open, onClose, result, entityNoun }: BulkResultDialogProps) {
  const { updatedCount, skippedByReason, total } = useMemo(() => {
    const results = result?.results ?? [];
    const updated = results.filter((r) => r.status === 'updated');
    const skipped = results.filter((r) => r.status === 'skipped');
    // Group skipped ids under their reason (fall back to a generic label).
    const grouped = new Map<string, string[]>();
    for (const r of skipped) {
      const reason = r.reason || 'Skipped';
      const list = grouped.get(reason) ?? [];
      list.push(r.id);
      grouped.set(reason, list);
    }
    return {
      updatedCount: updated.length,
      skippedByReason: [...grouped.entries()],
      total: results.length,
    };
  }, [result]);

  const skippedCount = total - updatedCount;
  const noun = (n: number) => `${entityNoun}${n === 1 ? '' : 's'}`;

  return (
    <Modal
      open={open}
      onClose={onClose}
      title="Bulk update results"
      size="md"
      footer={<Button onClick={onClose}>Done</Button>}
    >
      {result && (
        <div className="space-y-4">
          {/* Summary line */}
          <p className="text-data-md text-on-surface">
            {updatedCount} of {total} {noun(total)} updated
            {skippedCount > 0 ? `, ${skippedCount} skipped` : ''}.
          </p>

          {/* Updated */}
          <div className="flex items-start gap-2 rounded bg-success/10 px-3 py-2">
            <CheckCircle2 className="mt-0.5 h-4 w-4 shrink-0 text-success" />
            <span className="text-data-md text-on-surface">
              <span className="font-semibold">{updatedCount}</span> {noun(updatedCount)} updated
              successfully.
            </span>
          </div>

          {/* Skipped, grouped by reason (Req 8.4) */}
          {skippedByReason.length > 0 && (
            <div className="space-y-2">
              <div className="flex items-center gap-2 text-label-caps uppercase tracking-wider text-on-surface-variant">
                <SkipForward className="h-4 w-4" />
                Skipped ({skippedCount})
              </div>
              <ul className="space-y-2">
                {skippedByReason.map(([reason, ids]) => (
                  <li
                    key={reason}
                    className="rounded border border-outline bg-surface-variant/40 px-3 py-2"
                  >
                    <div className="flex items-center justify-between gap-2">
                      <span className="text-data-md font-medium text-on-surface">{reason}</span>
                      <span className="shrink-0 rounded-full bg-surface-variant px-2 py-0.5 text-body-sm text-on-surface-variant">
                        {ids.length} {noun(ids.length)}
                      </span>
                    </div>
                  </li>
                ))}
              </ul>
            </div>
          )}
        </div>
      )}
    </Modal>
  );
}
